import RootStore from '@App/stores/root-store'
import { ResultPage } from '@Entities/result-page'
import { SuggestedMatch } from '@Entities/matching/suggested-match'
import { SuggestionStatus } from '@Components/MatchComponents/v2/suggestion-status'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { RecordingGroup } from '@Entities/matching/recording-group-details'
import {
    getMatchSuggestion,
    getMatchSuggestionHistory,
    getRecordingGroupsDetails,
    getSongwriterMatchSuggestionHistory,
    getSongwriterRecordingGroupsDetails,
    getSongwriterSingleMatchSuggestionHistory,
} from '@App/api/routes/rightsholder/matching'
import { action, computed, makeObservable, observable, observableRef, runInAction } from 'mobx'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import RecordingGroupCategory from '@App/enum/recording-group-category'
import { RECORDING_GROUP_STATUS, RecordingGroupStatus } from '@Components/MatchComponents/v2/recording-group-status'
import { findPageOnLimitChange } from '@App/helpers/pagination/find-page-on-limit-change'
import { SubmissionFilterInputs } from '@Components/MatchComponents/v2/SubmissionFilterSection'

export const checkboxDefaults: SubmissionFilterInputs = {
    SUBMITTED: false,
    ACCEPTED: false,
    PARTIALLY_ACCEPTED: false,
    REJECTED: false,
    IN_PROGRESS: false,
    SUPERSEDED: false,
}

export class MatchHistoryStore {
    private static SUGGESTION_HISTORY_RESULTS_LIMIT_DEFAULT = 10
    private static SUGGESTION_HISTORY_RESULTS_PAGINATION_DEFAULT = {
        limit: MatchHistoryStore.SUGGESTION_HISTORY_RESULTS_LIMIT_DEFAULT,
        page: 1,
    }

    private static RECORDING_GROUP_RESULTS_LIMIT_DEFAULT = 10

    private rootStore: RootStore

    private _matchSuggestionHistory: ResultPage<SuggestedMatch>

    /** The filter being edited in the sidebar; only becomes the searched filter once applied. */
    private _matchSuggestionHistoryFilter: Record<SuggestionStatus, boolean | string> = checkboxDefaults

    private _matchSuggestionHistoryAppliedFilter: Record<SuggestionStatus, boolean | string> = checkboxDefaults

    /** Exact query key `_matchSuggestionHistory` was fetched under, so a remount only reuses it when it still matches. */
    private _lastResultsKey: unknown[] | undefined

    private _matchSuggestionHistoryPagination: PaginationInfo

    private _matchSuggestionHistoryPaginationBody: PaginationBody =
        MatchHistoryStore.SUGGESTION_HISTORY_RESULTS_PAGINATION_DEFAULT

    private _matchSuggestionHistoryResultsLimit: number = MatchHistoryStore.SUGGESTION_HISTORY_RESULTS_LIMIT_DEFAULT

    private _selectedMatchSuggestion: SuggestedMatch | null

    private _recordingGroupsResultsLimit = MatchHistoryStore.RECORDING_GROUP_RESULTS_LIMIT_DEFAULT

    private _recordingGroupsDetails: {
        pending?: ResultPage<RecordingGroup>
        accepted?: ResultPage<RecordingGroup>
        rejected?: ResultPage<RecordingGroup>
    } = {}

    private _recordingGroupsDetailsPagination: {
        pending?: PaginationInfo
        accepted?: PaginationInfo
        rejected?: PaginationInfo
    } = {}

    constructor(rootStore: RootStore) {
        makeObservable<
            MatchHistoryStore,
            | '_matchSuggestionHistory'
            | '_matchSuggestionHistoryFilter'
            | '_matchSuggestionHistoryAppliedFilter'
            | '_matchSuggestionHistoryPagination'
            | '_matchSuggestionHistoryPaginationBody'
            | '_matchSuggestionHistoryResultsLimit'
            | '_lastResultsKey'
            | '_selectedMatchSuggestion'
            | '_recordingGroupsDetails'
        >(this, {
            _matchSuggestionHistory: observable,
            _matchSuggestionHistoryFilter: observable,
            _matchSuggestionHistoryAppliedFilter: observableRef,
            _matchSuggestionHistoryPagination: observableRef,
            _matchSuggestionHistoryPaginationBody: observableRef,
            _matchSuggestionHistoryResultsLimit: observable,
            _lastResultsKey: observableRef,
            _selectedMatchSuggestion: observable,
            _recordingGroupsDetails: observable,
            setMatchSuggestionHistoryFilter: action,
            applyMatchSuggestionHistoryFilter: action,
            setMatchSuggestionHistoryPaginationBody: action,
            setMatchSuggestionHistoryResultsLimit: action,
            applyMatchSuggestionHistory: action,
            matchSuggestionHistoryFilterCount: computed,
            resetSelectedMatch: action,
            resetStore: action,
        })
        this.rootStore = rootStore
    }

    get matchSuggestionHistory() {
        return this._matchSuggestionHistory
    }

    get matchSuggestionHistoryPagination() {
        return this._matchSuggestionHistoryPagination
    }

    get matchSuggestionHistoryPaginationBody() {
        return this._matchSuggestionHistoryPaginationBody
    }

    get matchSuggestionHistoryResultsLimit() {
        return this._matchSuggestionHistoryResultsLimit
    }

    get selectedMatch() {
        return this._selectedMatchSuggestion
    }

    get recordingGroupsDetails() {
        return this._recordingGroupsDetails
    }

    get recordingGroupsDetailsPagination() {
        return this._recordingGroupsDetailsPagination
    }

    get recordingGroupsResultsLimit() {
        return this._recordingGroupsResultsLimit
    }

    get matchSuggestionHistoryFilter() {
        return this._matchSuggestionHistoryFilter
    }

    get matchSuggestionHistoryFilterCount(): number {
        return Object.values(this._matchSuggestionHistoryFilter).filter((value) => value || typeof value === 'string')
            .length
    }

    /** Status names of the applied (searched) filter, as the API expects them. */
    get appliedStatusFilters(): string[] {
        return Object.entries(this._matchSuggestionHistoryAppliedFilter)
            .filter(([, value]) => value === true)
            .map(([key]) => key)
    }

    get lastResultsKey(): unknown[] | undefined {
        return this._lastResultsKey
    }

    /**
     * Performs the request without touching any observable state, so it can be owned by an external
     * cache (TanStack Query). Pass a songwriter id for the Songwriter Hub, otherwise the current rightsholder is used.
     */
    async fetchMatchSuggestionHistory(
        statusFilters: string[],
        pagination: PaginationBody,
        songwriterId?: string
    ): Promise<ResultPage<SuggestedMatch>> {
        const response = songwriterId
            ? await getSongwriterMatchSuggestionHistory(songwriterId, statusFilters, 'last_updated_date', pagination)
            : await getMatchSuggestionHistory(
                  this.rootStore.rightsholderStore.currentRightsholderId,
                  statusFilters,
                  pagination
              )
        return {
            ...response.data,
            content: response.data.content.map((suggestion) => ({
                ...suggestion,
                getId: () => suggestion.suggestedMatchId,
            })),
        }
    }

    /** Stores results fetched elsewhere so components still reading the store stay in sync. */
    applyMatchSuggestionHistory(
        response: ResultPage<SuggestedMatch>,
        paginationBody: PaginationBody,
        resultsKey: unknown[]
    ): void {
        this._matchSuggestionHistory = response
        this._matchSuggestionHistoryPagination = calcNumPages(
            response.totalElements,
            this._matchSuggestionHistoryResultsLimit,
            paginationBody.page ? paginationBody.page : 1,
            false
        )
        this._lastResultsKey = resultsKey
    }

    reportSearchError(error: unknown): void {
        this.rootStore.popupStore.setAlertFromError(error)
    }

    /** Makes the sidebar's filter the searched one, and goes back to the first page. */
    applyMatchSuggestionHistoryFilter(): void {
        this._matchSuggestionHistoryAppliedFilter = { ...this._matchSuggestionHistoryFilter }
        this._matchSuggestionHistoryPaginationBody = { page: 1, limit: this._matchSuggestionHistoryResultsLimit }
    }

    async loadSongwriterRecordingGroupDetails(
        songwriterId: string,
        submissionId: number,
        recordingGroupCategory: RecordingGroupCategory,
        pagination: PaginationBody
    ): Promise<void> {
        const recordingGroupStatus: RecordingGroupStatus[] = []
        switch (recordingGroupCategory) {
            case RecordingGroupCategory.Pending:
                recordingGroupStatus.push(RECORDING_GROUP_STATUS.SUBMITTED)
                break
            case RecordingGroupCategory.Accepted:
                recordingGroupStatus.push(RECORDING_GROUP_STATUS.ACCEPTED, RECORDING_GROUP_STATUS.SUPERSEDED)
                break
            case RecordingGroupCategory.Rejected:
                recordingGroupStatus.push(RECORDING_GROUP_STATUS.REJECTED)
        }

        const response = await getSongwriterRecordingGroupsDetails(
            songwriterId,
            submissionId,
            recordingGroupStatus,
            pagination
        )
        runInAction(() => {
            this._recordingGroupsDetails[recordingGroupCategory] = response.data
            this._recordingGroupsDetailsPagination[recordingGroupCategory] = calcNumPages(
                response.data.totalElements,
                pagination.limit ? pagination.limit : MatchHistoryStore.RECORDING_GROUP_RESULTS_LIMIT_DEFAULT,
                pagination.page ? pagination.page : 0,
                false
            )
        })
    }

    async loadSongwriterSuggestedMatch(songwriterId: string, suggestionId: number): Promise<void> {
        let found =
            this.matchSuggestionHistory &&
            this.matchSuggestionHistory.content.find((match) => match.suggestedMatchId === suggestionId)
        if (!found) {
            const result = await getSongwriterSingleMatchSuggestionHistory(songwriterId, suggestionId)
            found = result.data
        }
        runInAction(() => {
            if (found) {
                this._selectedMatchSuggestion = found
            }
        })
    }

    setMatchSuggestionHistoryPaginationBody(body: PaginationBody): void {
        this._matchSuggestionHistoryPaginationBody = body
    }

    setMatchSuggestionHistoryResultsLimit(newLimit: number): void {
        const currentPagination = this.matchSuggestionHistoryPaginationBody
        const newPage = findPageOnLimitChange(currentPagination, this.matchSuggestionHistory.totalElements, newLimit, 0)
        this._matchSuggestionHistoryResultsLimit = newLimit
        this.setMatchSuggestionHistoryPaginationBody({
            ...currentPagination,
            limit: newLimit,
            page: newPage,
        })
    }

    setMatchSuggestionHistoryFilter(filters: Record<SuggestionStatus, boolean | string>): void {
        this._matchSuggestionHistoryFilter = filters
    }

    async loadSuggestedMatch(suggestionId: number): Promise<void> {
        // check for existing match suggestion in the loaded data
        let found =
            this.matchSuggestionHistory &&
            this.matchSuggestionHistory.content.find((match) => match.suggestedMatchId === suggestionId)
        if (!found) {
            // if it hasn't been loaded yet, load it.
            const result = await getMatchSuggestion(
                this.rootStore.rightsholderStore.currentRightsholderId,
                suggestionId
            )
            found = result.data
        }
        runInAction(() => {
            if (found) {
                this._selectedMatchSuggestion = found
            }
        })
    }

    async loadRecordingGroupDetails(
        submissionId: number,
        recordingGroupCategory: RecordingGroupCategory,
        pagination: PaginationBody
    ): Promise<void> {
        // TODO: This is 0-based pagination, but we use 1-based w/ PHP backend, what do?

        const recordingGroupStatus: RecordingGroupStatus[] = []
        switch (recordingGroupCategory) {
            case RecordingGroupCategory.Pending:
                recordingGroupStatus.push(RECORDING_GROUP_STATUS.SUBMITTED)
                break
            case RecordingGroupCategory.Accepted:
                recordingGroupStatus.push(RECORDING_GROUP_STATUS.ACCEPTED, RECORDING_GROUP_STATUS.SUPERSEDED)
                break
            case RecordingGroupCategory.Rejected:
                recordingGroupStatus.push(RECORDING_GROUP_STATUS.REJECTED)
        }

        const response = await getRecordingGroupsDetails(
            this.rootStore.rightsholderStore.currentRightsholderId,
            submissionId,
            recordingGroupStatus,
            pagination
        )
        runInAction(() => {
            this._recordingGroupsDetails[recordingGroupCategory] = response.data
            this._recordingGroupsDetailsPagination[recordingGroupCategory] = calcNumPages(
                response.data.totalElements,
                pagination.limit ? pagination.limit : MatchHistoryStore.RECORDING_GROUP_RESULTS_LIMIT_DEFAULT,
                pagination.page ? pagination.page : 0,
                false
            )
        })
    }

    setRecordingGroupResultsLimit(limit: number): void {
        this._recordingGroupsResultsLimit = limit
    }

    resetSelectedMatch(): void {
        runInAction(() => {
            this._selectedMatchSuggestion = null
            this._recordingGroupsDetails = {}
            this._recordingGroupsDetailsPagination = {}
        })
    }

    resetStore(): void {
        this.resetSelectedMatch()
        runInAction(() => {
            this._matchSuggestionHistory = {
                content: [],
                pageable: {
                    sort: { empty: true, unsorted: true, sorted: false },
                    offset: 0,
                    pageNumber: 0,
                    pageSize: 0,
                    paged: false,
                    unpaged: true,
                },
                last: true,
                totalPages: 0,
                totalElements: 0,
                sort: { empty: true, unsorted: true, sorted: false },
                first: true,
                number: 0,
                size: 0,
                numberOfElements: 0,
                empty: true,
            }
            this._matchSuggestionHistoryFilter = checkboxDefaults
            this._matchSuggestionHistoryAppliedFilter = checkboxDefaults
            this._lastResultsKey = undefined
            this._matchSuggestionHistoryPagination = { currentPage: 0, lastPage: 0 }
            this._matchSuggestionHistoryResultsLimit = MatchHistoryStore.SUGGESTION_HISTORY_RESULTS_LIMIT_DEFAULT
            this._matchSuggestionHistoryPaginationBody = MatchHistoryStore.SUGGESTION_HISTORY_RESULTS_PAGINATION_DEFAULT
            this._selectedMatchSuggestion = null
            this._recordingGroupsResultsLimit = MatchHistoryStore.RECORDING_GROUP_RESULTS_LIMIT_DEFAULT
            this._recordingGroupsDetails = {}
            this._recordingGroupsDetailsPagination = {}
        })
    }
}
